import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useBusiness } from '../context/BusinessContext';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { api, SettlementStatement, VenueSettlementSummary } from '../api/client';
import DashboardLayout from '../components/DashboardLayout';
import PlatformSettlementPanel from './payments/PlatformSettlementPanel';
import EarningsPanel from './payments/EarningsPanel';

const PORTAL_BASE_URL = window.location.origin;

function centsToDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDate(iso: string, timezone: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: timezone,
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function getPreviousMonths(count: number): string[] {
  const months: string[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    months.push(d.toISOString().slice(0, 7));
  }
  return months;
}

// ============================================
// Types
// ============================================

type StripeStatus = {
  connectedAccountId: string | null;
  onboardingStatus: string | null;
  detailsSubmitted: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  requirements: Record<string, any> | null;
  feeBps: number | null;
} | null;

type MsaPacket = {
  id: number;
  status: string;
  commissionRateBps: number;
  effectiveDate: string;
  signedAt: string | null;
  signerName: string | null;
  docs: {
    masterAgreement: string;
    dataPrivacy: string;
    bookingOperations: string;
    marketingConsent: string;
    signaturePage: string;
  };
} | null;

// ============================================
// Icons
// ============================================

const CheckCircleIcon = ({ size = 20, color = '#16a34a' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" stroke={color} strokeWidth="2" />
    <path d="M6 10.5L8.5 13L14 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WarningIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 2L18.66 17H1.34L10 2Z" stroke="#d97706" strokeWidth="1.5" fill="#fef3c7" strokeLinejoin="round" />
    <path d="M10 8V11M10 13.5V14" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M6 1H2.5C1.67 1 1 1.67 1 2.5V11.5C1 12.33 1.67 13 2.5 13H11.5C12.33 13 13 12.33 13 11.5V8M8 1H13M13 1V6M13 1L6 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StripeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <rect width="20" height="20" rx="4" fill="#635bff" />
    <path d="M9.3 7.7c0-.6.5-.9 1.3-.9.9 0 2 .3 2.9.8V5.2c-1-.4-2-.5-2.9-.5-2.4 0-3.9 1.2-3.9 3.3 0 3.2 4.4 2.7 4.4 4.1 0 .7-.6 1-1.4 1-1 0-2.3-.5-3.3-1.1v2.5c1.1.5 2.3.7 3.3.7 2.4 0 4-1.2 4-3.3C13.7 8.5 9.3 9.1 9.3 7.7Z" fill="white" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const RocketIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z" />
    <path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);

// ============================================
// Step indicator
// ============================================

function StepIndicator({ step, currentStep, label, description }: {
  step: number;
  currentStep: number;
  label: string;
  description: string;
}) {
  const isComplete = currentStep > step;
  const isCurrent = currentStep === step;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '16px', flex: 1,
      opacity: !isCurrent && !isComplete ? 0.45 : 1,
      transition: 'opacity 0.3s ease',
    }}>
      <div className={`go-live-step-circle ${isComplete ? 'go-live-step-circle--complete' : isCurrent ? 'go-live-step-circle--active' : ''}`}>
        {isComplete ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <span>{step}</span>
        )}
      </div>
      <div>
        <div style={{
          fontSize: '14px', fontWeight: 600,
          color: isCurrent ? 'var(--gray-900)' : isComplete ? 'var(--success)' : 'var(--gray-500)',
        }}>
          {label}
        </div>
        <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
          {isComplete ? 'Completed' : description}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Document Viewer
// ============================================

function DocumentViewer({ docs, activeDoc, onDocChange }: {
  docs: NonNullable<MsaPacket>['docs'] | undefined;
  activeDoc: string;
  onDocChange: (doc: string) => void;
}) {
  if (!docs) return null;

  const docTabs = [
    { key: 'masterAgreement', label: 'Master Agreement' },
    { key: 'dataPrivacy', label: 'Data & Privacy' },
    { key: 'bookingOperations', label: 'Booking Operations' },
    { key: 'marketingConsent', label: 'Marketing & Brand' },
    { key: 'signaturePage', label: 'Signature Page' },
  ];

  const content = (docs as any)[activeDoc] || '';

  return (
    <div>
      {/* Doc tab bar */}
      <div className="go-live-doc-tabs">
        {docTabs.map(tab => (
          <button
            key={tab.key}
            className={`go-live-doc-tab ${activeDoc === tab.key ? 'go-live-doc-tab--active' : ''}`}
            onClick={() => onDocChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Document content */}
      <div className="go-live-doc-content">
        <div
          className="go-live-doc-markdown"
          dangerouslySetInnerHTML={{ __html: markdownToHtml(content) }}
        />
      </div>
    </div>
  );
}

/**
 * Simple markdown-to-HTML for rendering MSA documents.
 * Handles headers, bold, italic, lists, tables, horizontal rules, paragraphs.
 */
function markdownToHtml(md: string): string {
  if (!md) return '';
  let html = md
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Headers
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Horizontal rules
    .replace(/^---$/gm, '<hr />')
    // Bold + italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    // Simple tables — convert markdown tables to HTML
    .replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(c => c.trim());
      if (cells.every(c => /^[-\s:]+$/.test(c))) return ''; // separator row
      const tag = 'td';
      return '<tr>' + cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('') + '</tr>';
    });

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');
  // Wrap consecutive <tr> in <table>
  html = html.replace(/((?:<tr>.*<\/tr>\n?)+)/g, '<table>$1</table>');

  // Paragraphs: wrap remaining text blocks
  html = html
    .split('\n\n')
    .map(block => {
      const trimmed = block.trim();
      if (!trimmed) return '';
      if (trimmed.startsWith('<')) return trimmed;
      return `<p>${trimmed.replace(/\n/g, '<br />')}</p>`;
    })
    .join('\n');

  return html;
}

// ============================================
// Main Component
// ============================================

export default function GoLiveScreen() {
  const { business, loading: bizLoading, needsVenueSelection } = useBusiness();
  const { isSuperAdmin, user } = useAuth();
  const { showConfirm, showAlert } = useModal();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Superadmin must select a venue first
  useEffect(() => {
    if (needsVenueSelection && isSuperAdmin) {
      navigate('/superadmin');
    }
  }, [needsVenueSelection, isSuperAdmin, navigate]);

  // MSA state
  const [msaPacket, setMsaPacket] = useState<MsaPacket>(null);
  const [msaLoading, setMsaLoading] = useState(true);
  const [activeDoc, setActiveDoc] = useState('masterAgreement');
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [signing, setSigning] = useState(false);

  // Stripe state
  const [stripeStatus, setStripeStatus] = useState<StripeStatus>(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  // Earnings state
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [earnings, setEarnings] = useState<any>(null);
  const [earningsLoading, setEarningsLoading] = useState(true);

  // Platform-collect settlement state (monthly transfer summary + history)
  const [settlement, setSettlement] = useState<VenueSettlementSummary | null>(null);
  const [statements, setStatements] = useState<SettlementStatement[]>([]);

  const months = getPreviousMonths(6);

  // Document scroll ref
  const docScrollRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // Derived states
  const msaSigned = msaPacket?.status === 'signed';
  const isPlatformCollect = business?.paymentRouting === 'platform';
  // Platform-collect venues don't need Stripe Connect — PickleCourts settles by
  // check from the ledger. Treat that as a green payments step.
  const stripeComplete = isPlatformCollect
    || Boolean(stripeStatus?.chargesEnabled && stripeStatus?.payoutsEnabled);
  const isLive = business?.isPublished;

  // Determine current step
  let currentStep = 1;
  if (msaSigned) currentStep = 2;
  if (msaSigned && stripeComplete) currentStep = 3;
  if (msaSigned && stripeComplete && isLive) currentStep = 4; // All done

  const commissionPct = stripeStatus?.feeBps != null
    ? (stripeStatus.feeBps / 100).toFixed(0)
    : null;

  // ============================================
  // Data loading
  // ============================================

  const loadMsa = useCallback(async () => {
    try {
      const { packet } = await api.getMsaCurrent();
      setMsaPacket(packet);
    } catch {
      setMsaPacket(null);
    } finally {
      setMsaLoading(false);
    }
  }, []);

  const loadStripeStatus = useCallback(async () => {
    try {
      const status = await api.getStripeConnectStatus();
      setStripeStatus(status);
    } catch {
      setStripeStatus(null);
    } finally {
      setStripeLoading(false);
    }
  }, []);

  const loadEarnings = useCallback(async (month: string) => {
    setEarningsLoading(true);
    try {
      const data = await api.getPaymentsEarnings(month);
      setEarnings(data);
    } catch {
      setEarnings(null);
    } finally {
      setEarningsLoading(false);
    }
  }, []);

  const loadSettlement = useCallback(async () => {
    if (!business?.id) return;
    try {
      setSettlement(await api.getVenueSettlementSummary(business.id));
    } catch {
      setSettlement(null);
    }
    try {
      const { statements: rows } = await api.getVenueSettlementStatements(business.id);
      setStatements(rows);
    } catch {
      setStatements([]);
    }
  }, [business?.id]);

  useEffect(() => {
    if (searchParams.get('stripe_return') || searchParams.get('stripe_refresh')) {
      loadStripeStatus();
    }
  }, [searchParams, loadStripeStatus]);

  useEffect(() => {
    if (!bizLoading) {
      loadMsa();
      loadStripeStatus();
      loadEarnings(selectedMonth);
      loadSettlement();
    }
  }, [bizLoading, loadMsa, loadStripeStatus, loadEarnings, loadSettlement, selectedMonth]);

  // ============================================
  // Handlers
  // ============================================

  const handleSign = async () => {
    if (!msaPacket || !signerName.trim() || !signerEmail.trim() || !acceptTerms) return;

    const confirmed = await showConfirm(
      'Sign Agreement',
      `By signing, you confirm that you are authorized to enter into this agreement on behalf of your venue. This is a legally binding document.\n\nSigner: ${signerName}\nEmail: ${signerEmail}`,
      { confirmLabel: 'Sign Agreement', type: 'warning' }
    );
    if (!confirmed) return;

    setSigning(true);
    try {
      await api.signMsa(msaPacket.id, signerName.trim(), signerEmail.trim());
      await loadMsa();
      await showAlert('Agreement Signed', 'Your Master Service Agreement has been signed successfully. You can now proceed to connect your Stripe account.', 'success');
    } catch (err: any) {
      await showAlert('Error', err?.message || 'Failed to sign agreement', 'error');
    } finally {
      setSigning(false);
    }
  };

  const handleStartStripeOnboarding = async () => {
    setOnboardingLoading(true);
    try {
      if (!stripeStatus?.connectedAccountId) {
        await api.createStripeConnectAccount();
      }
      const returnUrl = `${PORTAL_BASE_URL}/go-live?stripe_return=1`;
      const refreshUrl = `${PORTAL_BASE_URL}/go-live?stripe_refresh=1`;
      const { url } = await api.getStripeOnboardingLink(returnUrl, refreshUrl);
      window.location.href = url;
    } catch (err: any) {
      await showAlert('Error', err.message || 'Failed to start Stripe onboarding', 'error');
      setOnboardingLoading(false);
    }
  };

  // ============================================
  // Loading state
  // ============================================

  if (bizLoading) {
    return (
      <DashboardLayout>
        <div className="loading" style={{ minHeight: '400px' }}><div className="spinner" /></div>
      </DashboardLayout>
    );
  }

  // ============================================
  // Render
  // ============================================

  const renderStepContent = () => {
    // Step 1: MSA
    if (currentStep === 1) {
      if (msaLoading) {
        return (
          <div className="card" style={{ padding: '48px' }}>
            <div className="loading" style={{ minHeight: '100px' }}><div className="spinner" /></div>
          </div>
        );
      }

      if (!msaPacket) {
        return (
          <div className="go-live-empty-card">
            <div className="go-live-empty-icon">
              <DocumentIcon />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: 'var(--gray-900)' }}>
              No agreement ready yet
            </h3>
            <p style={{ margin: '0 0 4px', fontSize: '14px', color: 'var(--gray-500)', lineHeight: '1.6', maxWidth: '440px' }}>
              Your partnership agreement is being prepared by the PickleCourts team. Once it's ready, you'll be able to review and sign it right here.
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--gray-400)' }}>
              Questions? Reach out to <strong>support@picklecourts.rent</strong>
            </p>
          </div>
        );
      }

      return (
        <div className="go-live-msa-container">
          {/* Agreement header */}
          <div className="go-live-msa-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="go-live-msa-header-icon">
                <DocumentIcon />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>
                  Master Venue Partnership Agreement
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--gray-500)' }}>
                  Effective {new Date(msaPacket.effectiveDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  {msaPacket.commissionRateBps === 0
                    ? ' · Partner rate (0% commission)'
                    : ` · ${(msaPacket.commissionRateBps / 100)}% commission`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Document viewer */}
          <DocumentViewer
            docs={msaPacket.docs}
            activeDoc={activeDoc}
            onDocChange={setActiveDoc}
          />

          {/* Signature form */}
          <div className="go-live-signature-form">
            <div className="go-live-signature-form-header">
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--gray-900)' }}>
                Sign this agreement
              </h4>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--gray-500)' }}>
                By typing your name below, you acknowledge that you have read and agree to all terms.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '20px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '6px', display: 'block' }}>
                  Full Legal Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Jane Smith"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  style={{ width: '100%', fontFamily: "'Georgia', 'Times New Roman', serif", fontSize: '16px', fontStyle: 'italic' }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '6px', display: 'block' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <label className="go-live-accept-checkbox" style={{ marginTop: '20px' }}>
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
              />
              <span style={{ fontSize: '13px', color: 'var(--gray-700)', lineHeight: '1.5' }}>
                I have read, understand, and agree to the Master Venue Partnership Agreement and all addenda.
                I am authorized to sign on behalf of the venue.
              </span>
            </label>

            <button
              className="go-live-sign-btn"
              onClick={handleSign}
              disabled={signing || !signerName.trim() || !signerEmail.trim() || !acceptTerms}
            >
              {signing ? (
                <>
                  <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
                  Signing...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 19l7-7 3 3-7 7-3-3z" />
                    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
                    <path d="M2 2l7.586 7.586" />
                    <circle cx="11" cy="11" r="2" />
                  </svg>
                  Sign Agreement
                </>
              )}
            </button>
          </div>
        </div>
      );
    }

    // Step 2: Stripe
    if (currentStep === 2) {
      if (stripeLoading) {
        return (
          <div className="card" style={{ padding: '48px' }}>
            <div className="loading" style={{ minHeight: '100px' }}><div className="spinner" /></div>
          </div>
        );
      }

      const isPending = stripeStatus?.connectedAccountId && !stripeComplete;
      const pendingRequirements: string[] = stripeStatus?.requirements?.currently_due || [];

      return (
        <div className="go-live-stripe-container">
          <div className="go-live-stripe-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <StripeIcon />
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>
                  Connect your Stripe account
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--gray-500)' }}>
                  Payments go directly to your bank account on each booking
                </p>
              </div>
            </div>
          </div>

          <div style={{ padding: '32px' }}>
            {/* Pending state */}
            {isPending && (
              <>
                {pendingRequirements.length > 0 && (
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: '12px',
                    padding: '16px 20px', background: '#fffbeb', borderRadius: '12px',
                    border: '1px solid #fde68a', marginBottom: '24px',
                  }}>
                    <WarningIcon />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400e', marginBottom: '8px' }}>
                        Almost there — Stripe needs a few more details
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#78350f', lineHeight: '1.7' }}>
                        {pendingRequirements.map((r: string) => (
                          <li key={r}>{r.replace(/_/g, ' ').replace(/\./g, ' > ')}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                <button
                  className="go-live-stripe-btn"
                  onClick={handleStartStripeOnboarding}
                  disabled={onboardingLoading}
                >
                  {onboardingLoading ? 'Redirecting...' : 'Continue Stripe Setup'}
                  <LinkIcon />
                </button>
              </>
            )}

            {/* Not connected state */}
            {!stripeComplete && !isPending && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div className="go-live-stripe-icon-circle">
                  <StripeIcon />
                </div>
                <h4 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 600, color: 'var(--gray-900)' }}>
                  Start accepting payments
                </h4>
                <p style={{ margin: '0 0 24px', fontSize: '14px', color: 'var(--gray-500)', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto', lineHeight: '1.6' }}>
                  Connect your Stripe account to receive customer payments directly. Funds are deposited to your bank account automatically.
                </p>
                <button
                  className="go-live-stripe-btn"
                  onClick={handleStartStripeOnboarding}
                  disabled={onboardingLoading}
                >
                  <StripeIcon />
                  {onboardingLoading ? 'Redirecting...' : 'Connect with Stripe'}
                </button>
                <p style={{ margin: '16px 0 0', fontSize: '12px', color: 'var(--gray-400)' }}>
                  You'll be redirected to Stripe to verify your identity and add your bank account
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Step 3: Go Live (or already live — show dashboard)
    return null; // Handled below with the dashboard
  };

  // ============================================
  // Earnings section (shown when step >= 3)
  // ============================================

  const renderEarnings = () => (
    <EarningsPanel
      earnings={earnings}
      earningsLoading={earningsLoading}
      selectedMonth={selectedMonth}
      months={months}
      timezone={business?.timezone || 'UTC'}
      onMonthChange={setSelectedMonth}
    />
  );

  return (
    <DashboardLayout>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1>{currentStep >= 4 ? 'Payments & Billing' : 'Billing'}</h1>
          <p>{currentStep >= 4 ? 'Your venue is live — manage payments and view earnings' : 'Complete these steps to start accepting bookings'}</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* Progress stepper (hide when fully live) */}
        {currentStep < 4 && (
          <div className="go-live-stepper">
            <StepIndicator step={1} currentStep={currentStep} label="Sign Agreement" description="Review and sign your MSA" />
            <div className="go-live-stepper-line" />
            <StepIndicator step={2} currentStep={currentStep} label="Connect Payments" description="Set up Stripe" />
            <div className="go-live-stepper-line" />
            <StepIndicator step={3} currentStep={currentStep} label="Publish" description="Publish your venue" />
          </div>
        )}

        {/* Current step content */}
        {renderStepContent()}

        {/* Step 3: Go Live celebration + publish */}
        {currentStep === 3 && (
          <div className="go-live-launch-card">
            <div className="go-live-launch-icon">
              <RocketIcon />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: 'var(--gray-900)' }}>
              You're ready to publish!
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '14px', color: 'var(--gray-500)', lineHeight: '1.6', maxWidth: '460px' }}>
              Your agreement is signed and payments are connected. Publish your venue to start appearing on PickleCourts and accept bookings from customers.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#15803d' }}>
                <CheckCircleIcon size={16} /> Agreement signed
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#15803d' }}>
                <CheckCircleIcon size={16} /> {isPlatformCollect ? 'Payments configured' : 'Stripe connected'}
              </div>
            </div>
            <p style={{ margin: '16px 0 0', fontSize: '13px', color: 'var(--gray-500)' }}>
              Head to <strong>Facility Setup</strong> to publish your venue when you're ready.
            </p>
          </div>
        )}

        {/* Completed state: payments summary + Earnings */}
        {currentStep >= 3 && stripeComplete && isPlatformCollect && (
          <>
            {/* Platform-collect: full settlement panel (monthly transfer
                summary + transfer history), NOT the Stripe Connect card —
                these venues have no working Connect account (any
                connectedAccountId is a leftover of abandoned onboarding), and
                that card's hardcoded "Charges Enabled" copy would be false. */}
            <PlatformSettlementPanel settlement={settlement} statements={statements} />

            {/* Earnings */}
            {renderEarnings()}
          </>
        )}

        {currentStep >= 3 && stripeComplete && !isPlatformCollect && (
          <>
            {/* Stripe status summary */}
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '20px 24px', borderBottom: '1px solid var(--gray-200)',
                flexWrap: 'wrap', gap: '12px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <StripeIcon />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)' }}>Stripe Connect</h3>
                </div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600,
                  background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0',
                }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.2)' }} />
                  Connected
                </div>
              </div>
              <div style={{ padding: '24px' }}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '12px',
                  padding: '16px 20px', background: 'var(--success-light)', borderRadius: '10px',
                  border: '1px solid #bbf7d0',
                }}>
                  <CheckCircleIcon />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#15803d', marginBottom: '4px' }}>
                      Payments are active
                    </div>
                    <div style={{ fontSize: '13px', color: '#166534', lineHeight: '1.5' }}>
                      Customer payments go directly to your Stripe account on each booking. Stripe handles the deposit to your bank on its standard payout schedule.
                    </div>
                  </div>
                </div>
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: '24px',
                  marginTop: '20px', padding: '16px 20px', background: 'var(--gray-50)', borderRadius: '10px',
                }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Account ID</div>
                    <code style={{ fontSize: '13px', color: 'var(--gray-700)', background: 'var(--gray-200)', padding: '2px 8px', borderRadius: '4px' }}>
                      {stripeStatus?.connectedAccountId}
                    </code>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Charges</div>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#15803d' }}>Enabled</span>
                  </div>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Bank Payouts</div>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#15803d' }}>Enabled</span>
                  </div>
                  {commissionPct !== null && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Commission</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: commissionPct === '0' ? '#15803d' : 'var(--gray-900)' }}>{commissionPct}%</span>
                        {commissionPct === '0' && (
                          <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', background: '#dcfce7', color: '#15803d' }}>Partner rate</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Earnings */}
            {renderEarnings()}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
